import { debounce } from 'lodash';
import { useCallback, useEffect } from 'react';

const METRIC_PANEL_TOOLTIP_EVENT = 'metric-panel-tooltip-event';
export const METRIC_LENS_TOOLTIP_EVENT = 'metric-lens-tooltip-event';
export const WIDGET_TOOLTIP_EVENT = 'widget-tooltip-event';

const dispatchTooltipEvent = debounce(({ tag, x, y, eventName }) => {
  const tooltipEvent: any = new CustomEvent(eventName, {
    detail: { x, y, tag },
  });
  document.dispatchEvent(tooltipEvent);
}, 50);

function useTooltipSync({ instance, tag, enabled = true, eventName = METRIC_PANEL_TOOLTIP_EVENT }) {
  // show or hide tooltip
  const handleTooltip = useCallback(
    ({ detail }: any) => {
      const option = instance.getOption();

      if (option && option._viewType === 'timeseries') {
        const { x, y, tag: triggerTag } = detail;
        if (triggerTag !== tag) {
          if (x && y) {
            // converting from x (time) to pixel position in grid
            // y is set as 0 as it's based on metric unit/axis
            const [offsetX, offsetY] = instance.convertToPixel('grid', [x, 0]);

            // on sync between line and bar chart, offsetY might become Infinity hence adding fallback to 10
            instance.dispatchAction({
              type: 'showTip',
              x: offsetX,
              y: offsetY === Infinity ? 10 : offsetY,
            });
          } else {
            instance.dispatchAction({ type: 'showTip', x, y });
            instance.dispatchAction({ type: 'hideTip' });
          }
        }
      }
    },
    [instance, tag],
  );

  // capture hover events
  useEffect(() => {
    if (instance) {
      instance.getZr().on('mousemove', (params) => {
        const { event } = params;
        const { offsetX, offsetY } = event;

        // convert from pixel to x-axis point
        const pointInPixel = [offsetX, offsetY];
        const pointInGrid = instance.convertFromPixel('grid', pointInPixel);

        const option = instance.getOption();
        if (option._viewType === 'timeseries') {
          dispatchTooltipEvent({ x: pointInGrid[0], y: offsetY, tag, eventName });
        }
      });

      // clear event has to trigger even if user moves to a distribution type chart
      instance.getZr().on('mouseout', () => {
        dispatchTooltipEvent({ x: 0, y: 0, tag, eventName });
      });
    }
  }, [instance, tag, eventName]);

  // tooltip global listener
  useEffect(() => {
    if (enabled) {
      document.addEventListener(eventName, handleTooltip);
    } else {
      // diag mode scenario
      document.removeEventListener(eventName, handleTooltip);
    }
    return () => {
      document.removeEventListener(eventName, handleTooltip);
      if (instance) {
        instance.dispatchAction({ type: 'hideTip' });
      }
    };
  }, [instance, handleTooltip, enabled, eventName]);
}

export default useTooltipSync;
